import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * MEMBERS-FIRST UNLOCK WINDOW (2026-10-14)
 *
 * Server truth: mkt_settings['members_first_unlock'] = { enabled, hours }.
 * Enforced ONLY server-side in mkt_claim_lead_core (reason 'members_first'):
 * for the first `hours` after a listing is created, only buyers with an ACTIVE
 * membership (mkt_subscriptions.status='active') can unlock it.
 * The browser only DISPLAYS the window. When disabled, nothing renders.
 *
 * Admins enable it in Admin → Settings → Members-first unlock window, and the
 * RPC refuses to enable it unless the 11-case self-test passed in the last 24 h.
 */
export const MEMBERS_FIRST_REASON = 'members_first';

export interface MembersFirstState {
  enabled: boolean;
  hours: number;
  endsAt: number | null;
  active: boolean;
  viewerIsMember: boolean;
}

const NIL = '00000000-0000-0000-0000-000000000000';
let cache: { at: number; p: Promise<MembersFirstState> } | null = null;

const parse = (d: any): MembersFirstState => {
  const endsAt = d?.ends_at ? new Date(d.ends_at).getTime() : null;
  return {
    enabled: d?.enabled === true,
    hours: Number(d?.hours) || 0,
    endsAt,
    active: d?.active === true && !!endsAt && endsAt > Date.now(),
    viewerIsMember: d?.viewer_is_member === true,
  };
};

const fetchState = async (listingId: string): Promise<MembersFirstState> => {
  try {
    const { data, error } = await supabase.rpc('mkt_listing_members_first', { p_listing_id: listingId });
    if (error) throw error;
    return parse(data);
  } catch {
    return { enabled: false, hours: 0, endsAt: null, active: false, viewerIsMember: false };
  }
};

/** Global config + viewer membership, cached for 60 s and shared by every card. */
export const getMembersFirstConfig = (): Promise<MembersFirstState> => {
  if (!cache || Date.now() - cache.at > 60000) cache = { at: Date.now(), p: fetchState(NIL) };
  return cache.p;
};

export const resetMembersFirstCache = () => { cache = null; };

/** Window end for a listing from its created_at, using the global config. */
export const endsAtFrom = (cfg: MembersFirstState, createdAt?: string | null): number | null => {
  if (!cfg.enabled || cfg.hours <= 0 || !createdAt) return null;
  const t = new Date(createdAt).getTime();
  return Number.isFinite(t) ? t + cfg.hours * 3600000 : null;
};

export const formatCountdown = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
};

/**
 * Countdown hook. With `listingId` the window end comes from the server
 * (mkt_listing_members_first); otherwise it is computed from `createdAt`.
 */
export const useMembersFirst = (opts: { listingId?: string; createdAt?: string | null }) => {
  const { listingId, createdAt } = opts;
  const [state, setState] = useState<MembersFirstState | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let alive = true;
    (async () => {
      const cfg = await getMembersFirstConfig();
      if (!cfg.enabled) { if (alive) setState(cfg); return; }
      if (listingId) {
        const s = await fetchState(listingId);
        if (alive) setState({ ...s, viewerIsMember: s.viewerIsMember || cfg.viewerIsMember });
        return;
      }
      const endsAt = endsAtFrom(cfg, createdAt);
      if (alive) setState({ ...cfg, endsAt, active: !!endsAt && endsAt > Date.now() });
    })();
    return () => { alive = false; };
  }, [listingId, createdAt]);

  const live = !!state?.active && !!state.endsAt && state.endsAt > now;
  useEffect(() => {
    if (!live) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [live]);

  return {
    active: live,
    remainingMs: live && state?.endsAt ? state.endsAt - now : 0,
    viewerIsMember: !!state?.viewerIsMember,
    hours: state?.hours || 0,
    endsAt: state?.endsAt || null,
  };
};
